<script setup lang="ts">
import { useRouteQuery } from '@vueuse/router'

const { locale, t } = useI18n()
const route = useRoute()

const searchQuery = useRouteQuery('q', '')
const searchQueryInput = ref(searchQuery.value)

const isSearchPage = computed(() => ((route?.name as string) ?? '').includes('search'))
const isCheckoutPage = computed(() => ((route?.name as string) ?? '').includes('checkout'))

watch(isSearchPage, (isSearchPage) => {
  if (!isSearchPage)
    searchQueryInput.value = ''
})

const days = computed(() => {
  const { 0: monday, 4: friday } = getWeekdays(locale.value, 'short')
  return `${monday} - ${friday}`
})
const hours = computed(() => {
  const { 6: six, 18: eighteen } = getHours(locale.value)
  return `${six} - ${eighteen}`
})
</script>

<template>
  <div class="relative h-10 w-full flex bg-primary hidden lg:block">
    <div class="h-full w-full flex flex-row items-center justify-around ws-nowrap px-12.5 py0">
      <span class="flex items-center px2.5 py0">
        <Icon name="lucide:gift" class="me2.5 size-4 text-white" />
        <span class="w-fit ws-break-spaces text-xs text-white fw700 uppercase">
          {{ t('incentives.gift_with_every_purchase.title') }}
        </span>
      </span>

      <span class="flex items-center px2.5 py0">
        <Icon name="lucide:clock" class="me2.5 size-4 text-white" />
        <span class="w-fit ws-break-spaces text-xs text-white fw700 uppercase">
          {{ t('customer_service.days_and_hours', { hours, days }) }}
        </span>
      </span>

      <span class="flex items-center px2.5 py0">
        <Icon name="lucide:shield-check" class="me2.5 size-4 text-white" />
        <span class="w-fit ws-break-spaces text-xs text-white fw700 uppercase">
          {{ t('product.shipping_guarantee.label') }}
        </span>
      </span>
    </div>
  </div>

  <NTinyCarousel :items="3" class="h-10 bg-primary text-white lg:hidden">
    <template #slide-0>
      <span class="box-border min-w-0 flex items-center justify-center bg-transparent px-0 py-3 text-xs fw600 uppercase">
        <Icon name="lucide:gift" class="relative me-0.5 box-border aspect-square size-4 object-contain" aria-hidden="true" />
        <span class="ms-2.5 fw700">{{ t('incentives.gift_with_every_purchase.title') }}</span>
      </span>
    </template>

    <template #slide-1>
      <span class="box-border min-w-0 flex items-center justify-center bg-transparent px-0 py-3 text-xs fw600 uppercase">
        <Icon name="lucide:clock" class="relative me-0.5 box-border aspect-square size-4 object-contain" aria-hidden="true" />
        <span class="ms-2.5 fw700">{{ t('customer_service.days_and_hours', { hours, days }) }} GST</span>
      </span>
    </template>

    <template #slide-2>
      <span class="box-border min-w-0 flex items-center justify-center bg-transparent px-0 py-3 text-xs fw600 uppercase">
        <Icon name="lucide:shield-check" class="relative me-0.5 box-border aspect-square size-4 object-contain" aria-hidden="true" />
        <span class="ms-2.5 fw700">{{ t('product.shipping_guarantee.label') }}</span>
      </span>
    </template>
  </NTinyCarousel>

  <LayoutHeader />

  <slot />

  <LazyNavDrawer />
  <LazyCartDrawer />

  <div class="flex-1" />

  <div v-if="!isCheckoutPage" class="mb6 bg-slate-50">
    <div class="mx-auto max-w-[88rem] py8 lg:px6 sm:px2 sm:py14">
      <MarketingIllustratedIncentives class="grid grid-cols-1 mx-auto max-w-2xl gap-x-8 gap-y-12 lg:grid-cols-2 lg:max-w-none lg:gap-y-16" />
    </div>
  </div>

  <div class="mx-auto max-w-3xl px-4 lg:max-w-[88rem] md:max-w-4xl lg:px6 sm:px-6 sm:px4">
    <UiCard class="flex flex-col items-center p-4 sm:flex-row sm:justify-between">
      <div class="mb-4 flex items-center sm:mb-0">
        <div class="relative mr-3 h-8 w-8">
          <Icon name="i-heroicons:chat-bubble-left-ellipsis" class="text-3xl" />
          <span class="absolute top-0 h-3 w-3 flex -right-1">
            <span class="absolute h-full w-full inline-flex animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span class="relative h-3 w-3 inline-flex rounded-full bg-emerald-500" />
          </span>
        </div>

        <div class="sm:ml4">
          <div class="flex items-center">
            <h4 class="text-lg font-semibold">
              {{ t('support_agent.need_assistance.label') }}
            </h4>
          </div>
          <!-- Subtitle -->
          <p class="text-sm text-slate-600 leading-6">
            {{ t('support_agent.hours_of_operation.label') }}
          </p>
        </div>
      </div>
    </UiCard>
  </div>

  <!-- App Banner -->
  <MarketingAppPromotionBanner />

  <!-- Footer -->
  <TheFooter />
</template>
